import React, { useState, useCallback, useEffect } from 'react';
import { compose } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { get, isEmpty } from 'lodash';
import { DetailsObjectType } from 'store/pages/loaders/loader-types';
import { playerActions } from 'store/player';
import { profileSelectors } from 'store/profile';
import { pagesActions } from 'store/pages';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import ScrollTopHack from 'components/ScrollTopHack';
import WithMouseControls from 'components/HOCs/WithMouseControls';
import useModals from 'hooks/useModals';
import { DetailsContext } from './DetailsContext';
import { DetailsInnerWrapper, DetailsWrapper } from './StyledComponents';
import MainContentSection from './MainContentSection';
import ExtraContentSection from './ExtraContentSection';
import getPCRating from 'utils/get-pc-rating';

export interface IDetailsProps {
  data: {
    details: DetailsObjectType;
    recommendations?: DetailsObjectType[];
  } | null;
  isLoading: boolean;
  lastFocus?: {
    save: (selector: string) => void;
  };
}
const BASE_MAIN_SECTION_OFFSET = 540;
const BASE_EXTRA_SECTION_OFFSET = 1440;

const Details: React.FC<IDetailsProps> = ({ data, isLoading }) => {
  const dispatch = useDispatch();
  const { showLoginModal, showPinModal } = useModals();
  const isLoggedIn = useSelector(profileSelectors.selectIsLoggedIn);
  const isParentalControlEnabled = useSelector(profileSelectors.selectIsParentalConterolEnabled);
  const minPCAvailableAge = useSelector(profileSelectors.selectParentalAge);
  const [pageOffset, setPageOffset] = useState(0);
  const contentId = get(data, ['details', 'id'], null);
  const isReadyForRender = data && !isLoading;

  const checkPC = useCallback(
    (action: () => void) => {
      const ageLimit = parseInt(get(getPCRating(data?.details.mpaa), ['age']), 10);

      if (minPCAvailableAge === 0 || !ageLimit || !isParentalControlEnabled) {
        action();
      } else if (ageLimit >= minPCAvailableAge) {
        showPinModal({ sectionToFocus: '@WATCH_CONTROLS' }, action);
      } else {
        action();
      }
    },
    [data?.details, isParentalControlEnabled, minPCAvailableAge, showPinModal],
  );

  const onPlay = useCallback(() => {
    const isAvailableToPlay = !isEmpty(data.details.entitlement);
    if (!isAvailableToPlay) {
      console.error('Not available to play!');
      return;
    }

    const action = () => dispatch(playerActions.navigateToPlayer({ item: data.details }));

    checkPC(action);
  }, [data?.details, dispatch, checkPC]);

  const onWatchlist = useCallback(() => {
    if (!isLoggedIn) return showLoginModal({ sectionToFocus: '@WATCH_CONTROLS' });

    dispatch(pagesActions.toggleFavoriteStatus(data.details));
  }, [data, dispatch, isLoggedIn, showLoginModal]);

  const onEpisodePlay = useCallback(
    (episode) => {
      const action = () =>
        dispatch(playerActions.navigateToPlayer({ item: data.details, episodeId: episode.id }));

      checkPC(action);
    },
    [checkPC, dispatch, data?.details],
  );

  useEffect(() => {
    // move focus at the top
    if (contentId) SpatialNavigationWrapper.focus('@WATCH_CONTROLS');
  }, [contentId]);

  return (
    <DetailsWrapper>
      <DetailsContext.Provider value={isReadyForRender ? data.details : null}>
        <DetailsInnerWrapper pageOffset={-pageOffset}>
          {isReadyForRender && (
            <MainContentSection
              data={data.details}
              pageOffset={pageOffset}
              baseFocusOffset={BASE_MAIN_SECTION_OFFSET}
              onItemFocus={setPageOffset}
              onPlay={onPlay}
              onFavorite={onWatchlist}
            />
          )}
          {isReadyForRender && (
            <ExtraContentSection
              recommendations={data.recommendations}
              seasons={data.details.series_seasons}
              baseFocusOffset={BASE_EXTRA_SECTION_OFFSET}
              onItemFocus={setPageOffset}
              onEpisodePlay={onEpisodePlay}
            />
          )}
        </DetailsInnerWrapper>
      </DetailsContext.Provider>
    </DetailsWrapper>
  );
};

export default compose<React.ComponentClass<IDetailsProps>>(
  ScrollTopHack,
  WithMouseControls,
)(Details);
